import React from 'react';
import {
  Box,
  Grid,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Tag,
  Divider,
  Tooltip,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAnalytics } from '../../../contexts/AnalyticsContext';

interface MoodDistribution {
  [mood: string]: number;
}

interface TagStat {
  id: string;
  encrypted_name: string;
  decrypted_name: string;
  color: string;
  entry_count: number;
  mood_distribution?: MoodDistribution;
}

interface TagCoOccurrence {
  tag1_id: string;
  tag2_id: string;
  occurrence_count: number;
}

interface TagStats {
  tags: TagStat[];
  coOccurrences: TagCoOccurrence[];
}

const TagStats: React.FC = () => {
  const { tagStats } = useAnalytics();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const gridColor = useColorModeValue('#E2E8F0', '#2D3748');
  const labelColor = useColorModeValue('#1A202C', '#E2E8F0');
  const tooltipBg = useColorModeValue('#FFFFFF', '#1A202C');
  const barColor = useColorModeValue('#3182CE', '#63B3ED');

  // Sort tags by entry count
  const sortedTags = [...tagStats.tags].sort((a, b) => b.entry_count - a.entry_count);

  const getMoodColor = (label: string) => {
    switch (label) {
      case 'Amazing': return 'yellow.400';
      case 'Happy': return 'green.400';
      case 'Good': return 'blue.400';
      case 'Okay': return 'purple.400';
      case 'Down': return 'red.400';
      default: return 'blue.400';
    }
  };

  const renderMoodDistribution = (tag: TagStat) => {
    if (!tag.mood_distribution) return null;

    return Object.entries(tag.mood_distribution).map(([mood, count]) => {
      const percentage = (count / tag.entry_count) * 100;
      return (
        <Tooltip key={mood} label={`${mood}: ${count} entries (${percentage.toFixed(1)}%)`}>
          <Box
            h="20px"
            flexGrow={count}
            bg={getMoodColor(mood)}
            borderRadius="sm"
          />
        </Tooltip>
      );
    });
  };

  return (
    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
      {/* Tag Usage Chart */}
      <Box
        p={6}
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        height="400px"
      >
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Tag Usage
        </Text>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={sortedTags.slice(0, 10)}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="decrypted_name"
              angle={-45}
              textAnchor="end"
              height={60}
              tick={{ fill: labelColor }}
            />
            <YAxis tick={{ fill: labelColor }} />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: tooltipBg,
                border: '1px solid #CBD5E0',
              }}
              labelStyle={{ color: labelColor }}
            />
            <Bar dataKey="entry_count" fill={barColor} name="Entries" />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Tag Mood Distribution */}
      <Box
        p={6}
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
      >
        <VStack spacing={6} align="stretch">
          {/* Mood Distribution */}
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Mood Distribution
            </Text>
            <VStack spacing={3} align="stretch">
              {sortedTags.slice(0, 5).map((tag) => (
                <Box key={tag.id}>
                  <HStack justify="space-between" mb={2}>
                    <Tag size="md" colorScheme="blue" variant="subtle">
                      {tag.decrypted_name}
                    </Tag>
                    <Text fontSize="sm">
                      {tag.entry_count} entries
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    {renderMoodDistribution(tag)}
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider />

          {/* Tag Co-occurrences */}
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Common Tag Pairs
            </Text>
            <VStack spacing={3} align="stretch">
              {tagStats.coOccurrences.slice(0, 5).map((coOccurrence) => {
                const tag1 = tagStats.tags.find((t) => t.id === coOccurrence.tag1_id);
                const tag2 = tagStats.tags.find((t) => t.id === coOccurrence.tag2_id);
                if (!tag1 || !tag2) return null;

                return (
                  <HStack key={`${tag1.id}-${tag2.id}`} justify="space-between">
                    <HStack>
                      <Tag size="sm" colorScheme="blue" variant="subtle">
                        {tag1.decrypted_name}
                      </Tag>
                      <Text>+</Text>
                      <Tag size="sm" colorScheme="blue" variant="subtle">
                        {tag2.decrypted_name}
                      </Tag>
                    </HStack>
                    <Text fontSize="sm">
                      {coOccurrence.occurrence_count} entries
                    </Text>
                  </HStack>
                );
              })}
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Grid>
  );
};

export default TagStats; 